import React, { useState, useEffect } from 'react';
import '../styles/Pages.css';
import '../styles/Games.css';
import GameMapBackground from '../components/GameMapBackground';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const renderGameZone = (game, isActive, onClick) => {
  const d = game.shapeData;
  const strokeColor = isActive ? 'white' : 'rgba(255,255,255,0.45)';
  const strokeW = isActive ? 2.5 : 1.5;

  if (game.shape === 'rect') {
    const fontSize = Math.min(d.w, d.h) * 0.5;
    return (
      <g key={game.id} className={`game-zone${isActive ? ' active' : ''}`} onClick={onClick}>
        <rect x={d.x} y={d.y} width={d.w} height={d.h} fill={game.color} fillOpacity={0.85} rx={4} stroke={strokeColor} strokeWidth={strokeW} />
        <text x={d.x + d.w / 2} y={d.y + d.h / 2} textAnchor="middle" dominantBaseline="middle" fontSize={fontSize} style={{ pointerEvents: 'none' }}>{game.emoji}</text>
        <text x={d.x + d.w / 2} y={d.y + d.h + 14} textAnchor="middle" fontSize={14} fill="rgba(255,255,255,0.85)" stroke="rgba(0,0,0,0.6)" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{game.name}</text>
      </g>
    );
  }

  if (game.shape === 'line') {
    const midX = (d.x1 + d.x2) / 2;
    const midY = (d.y1 + d.y2) / 2;
    return (
      <g key={game.id} className={`game-zone${isActive ? ' active' : ''}`} onClick={onClick}>
        <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke="transparent" strokeWidth={24} />
        <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={game.color} strokeWidth={2.5} strokeOpacity={0.6} strokeDasharray="5 4" />
        <circle cx={d.x1} cy={d.y1} r={7} fill={game.color} fillOpacity={0.9} stroke={strokeColor} strokeWidth={strokeW} />
        <circle cx={d.x2} cy={d.y2} r={7} fill={game.color} fillOpacity={0.9} stroke={strokeColor} strokeWidth={strokeW} />
        <text x={midX} y={midY - 11} textAnchor="middle" fontSize={11} style={{ pointerEvents: 'none' }}>{game.emoji}</text>
        <text x={midX} y={midY + 26} textAnchor="middle" fontSize={14} fill="rgba(255,255,255,0.85)" stroke="rgba(0,0,0,0.6)" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{game.name}</text>
      </g>
    );
  }

  if (game.shape === 'circle') {
    return (
      <g key={game.id} className={`game-zone${isActive ? ' active' : ''}`} onClick={onClick}>
        <circle cx={d.cx} cy={d.cy} r={d.r} fill={game.color} fillOpacity={0.85} stroke={strokeColor} strokeWidth={strokeW} />
        <text x={d.cx} y={d.cy} textAnchor="middle" dominantBaseline="middle" fontSize={d.r * 0.9} style={{ pointerEvents: 'none' }}>{game.emoji}</text>
        <text x={d.cx} y={d.cy + d.r + 14} textAnchor="middle" fontSize={14} fill="rgba(255,255,255,0.85)" stroke="rgba(0,0,0,0.6)" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{game.name}</text>
        {game.indoor && (
          <g style={{ pointerEvents: 'none' }}>
            <rect x={d.cx - 18} y={d.cy + d.r + 18} width={36} height={13} rx={3} fill="rgba(20,50,120,0.75)" />
            <text x={d.cx} y={d.cy + d.r + 27} textAnchor="middle" fontSize={7} fill="white" fontWeight="600">indoors</text>
          </g>
        )}
      </g>
    );
  }

  return null;
};

const GamesPage = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);

  useEffect(() => {
    fetch(`${API}/games`)
      .then(r => r.json())
      .then(data => setGames(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (game) => setActiveGame(prev => prev?.id === game.id ? null : game);

  return (
    <div className="page-container">
      <div className="page-content">
        <h2>The Games</h2>
        <p className="event-description" style={{ marginBottom: '1.5rem' }}>
          Click any game on the board to learn more about it.
        </p>

        {loading ? (
          <p style={{ color: '#888' }}>Loading…</p>
        ) : (
          <div className="game-board-wrap">
            <svg viewBox="0 0 460 560" className="game-board-svg" xmlns="http://www.w3.org/2000/svg">
              <GameMapBackground />
              {games.map(game => renderGameZone(game, activeGame?.id === game.id, () => handleClick(game)))}
            </svg>
          </div>
        )}

        {activeGame ? (
          <div className="game-info-panel" style={{ borderColor: activeGame.color }}>
            <div className="game-info-header" style={{ borderBottomColor: activeGame.color }}>
              <span className="game-info-emoji">{activeGame.emoji}</span>
              <div>
                <h3>{activeGame.name}</h3>
                {activeGame.indoor && <span className="game-indoor-badge">Played Indoors</span>}
              </div>
              <button className="game-info-close" onClick={() => setActiveGame(null)}>✕</button>
            </div>
            <div className="game-info-body">
              {activeGame.intro && <p className="rules-intro">{activeGame.intro}</p>}
              {activeGame.rules.map((section, i) => (
                <div key={i} className="rules-section">
                  <strong>{section.title}</strong>
                  <ul>{section.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="game-board-hint">Tap any game to see details</p>
        )}
      </div>
    </div>
  );
};

export default GamesPage;
